import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import type { KeywordDrift } from '@repo/shared-types';
import {
  useKeywordDrift,
  usePlatformConfig,
  usePurgeKeyword,
  useResyncKeyword,
  useRotateKeyword,
} from '../../hooks/usePlatformConfig';

/** MISSING_PRIMARY and EMPTY cost money or break reconciliation outright, so
 *  they read as alarms; EXTRA and MISSING_RETIRED are informational. */
const DRIFT_COPY: Record<KeywordDrift['status'], { tone: 'ok' | 'warn' | 'danger'; text: string }> = {
  OK: { tone: 'ok', text: 'Whitelist trên SePay khớp với cấu hình.' },
  EMPTY: {
    tone: 'danger',
    text: 'SePay đang để whitelist RỖNG, mọi giao dịch đều bắn webhook và bị tính 500đ. Đẩy lại ngay.',
  },
  MISSING_PRIMARY: {
    tone: 'danger',
    text: 'Từ khóa đang dùng KHÔNG có trên SePay, thanh toán mới không tự đối soát. Đẩy lại ngay.',
  },
  MISSING_RETIRED: {
    tone: 'warn',
    text: 'Một từ khóa đã nghỉ hưu không còn trên SePay, hóa đơn cũ mang từ đó sẽ không tự đối soát.',
  },
  EXTRA: { tone: 'warn', text: 'SePay có từ khóa lạ không do hệ thống đặt, ai đó đã sửa trực tiếp trên console.' },
};

const TONE_CLASS = {
  ok: 'border-green-200 bg-green-50 text-green-800',
  warn: 'border-amber-200 bg-amber-50 text-amber-800',
  danger: 'border-red-200 bg-red-50 text-red-800',
} as const;

export function KeywordTab() {
  const { data: cfg, isLoading } = usePlatformConfig();
  const { data: drift, isLoading: driftLoading } = useKeywordDrift();
  const rotate = useRotateKeyword();
  const resync = useResyncKeyword();
  const purge = usePurgeKeyword();
  const [next, setNext] = useState('');
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [confirmResync, setConfirmResync] = useState(false);
  const [purgeTarget, setPurgeTarget] = useState<string | null>(null);

  if (isLoading || !cfg) return <div className="p-6 text-surface-500">…</div>;

  const copy = drift ? DRIFT_COPY[drift.status] : null;
  // What POST /resync actually pushes (whitelistOf(cfg) server-side): the live
  // keyword followed by every retired one, in that order.
  const resyncWhitelist = [cfg.ipnKeyword, ...cfg.ipnKeywordsRetired];
  const resyncExtra = drift?.extra ?? [];

  return (
    <div className="space-y-6 max-w-2xl">
      <section className="rounded-lg border border-surface-200 bg-surface-0 p-5">
        <h3 className="text-sm font-semibold text-surface-800">Từ khóa đang dùng</h3>
        <p className="mt-1 font-mono text-lg font-semibold text-surface-900">{cfg.ipnKeyword}</p>
        <p className="mt-1 text-[13px] text-surface-500">
          Đóng dấu lên mọi QR học phí mới. SePay chỉ bắn webhook cho giao dịch có chứa một trong các từ khóa này.
        </p>
      </section>

      <section className="rounded-lg border border-surface-200 bg-surface-0 p-5">
        <h3 className="text-sm font-semibold text-surface-800">Đồng bộ với SePay</h3>
        {driftLoading ? (
          <p className="mt-2 flex items-center gap-2 text-[13px] text-surface-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Đang kiểm tra…
          </p>
        ) : copy ? (
          <div className={`mt-2 flex items-start gap-2 rounded-sm border px-3 py-2 text-[13px] ${TONE_CLASS[copy.tone]}`}>
            {copy.tone === 'ok' ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <div>
              <p>{copy.text}</p>
              <p className="mt-1 font-mono text-[12px] opacity-80">
                Hệ thống: [{drift!.ours.join(', ')}] · SePay: [{drift!.remote.join(', ') || '—'}]
              </p>
            </div>
          </div>
        ) : (
          <p className="mt-2 text-[13px] text-red-700">Không đọc được cấu hình từ SePay.</p>
        )}
        {drift && drift.status !== 'OK' && (
          <Button
            className="mt-3"
            variant="outline"
            disabled={resync.isPending}
            onClick={() => setConfirmResync(true)}
          >
            Đẩy lại lên SePay
          </Button>
        )}
      </section>

      <section className="rounded-lg border border-surface-200 bg-surface-0 p-5">
        <h3 className="text-sm font-semibold text-surface-800">Đổi từ khóa</h3>
        <p className="mt-1 text-[13px] text-surface-500">
          4–20 ký tự IN HOA hoặc số, không dấu, không khoảng trắng. Từ khóa cũ tự động chuyển sang danh sách nghỉ hưu
          để QR đã gửi cho phụ huynh vẫn đối soát được.
        </p>
        <div className="mt-3 flex gap-2">
          <Input
            value={next}
            onChange={(e) => setNext(e.target.value.toUpperCase())}
            placeholder="VD: HOCPHI"
            className="font-mono"
          />
          <Button disabled={!/^[A-Z0-9]{4,20}$/.test(next) || rotate.isPending} onClick={() => setConfirmRotate(true)}>
            Đổi
          </Button>
        </div>
      </section>

      <section className="rounded-lg border border-surface-200 bg-surface-0 p-5">
        <h3 className="text-sm font-semibold text-surface-800">Từ khóa đã nghỉ hưu</h3>
        {cfg.ipnKeywordsRetired.length === 0 ? (
          <p className="mt-1 text-[13px] text-surface-400">Chưa có.</p>
        ) : (
          <ul className="mt-2 space-y-1">
            {cfg.ipnKeywordsRetired.map((k) => (
              <li key={k} className="flex items-center justify-between text-[13px]">
                <span className="font-mono">{k}</span>
                <Button variant="ghost" size="sm" onClick={() => setPurgeTarget(k)} disabled={purge.isPending}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <AlertDialog open={confirmRotate} onOpenChange={(o) => !o && setConfirmRotate(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Đổi từ khóa sang {next}?</AlertDialogTitle>
            <AlertDialogDescription>
              Whitelist đẩy lên SePay sẽ là [{[next, cfg.ipnKeyword, ...cfg.ipnKeywordsRetired].join(', ')}]. QR đã gửi
              cho phụ huynh vẫn đối soát bình thường. Nếu SePay từ chối, cấu hình giữ nguyên.
              <br />
              <br />
              <strong>Lưu ý:</strong> SePay khớp từ khóa này theo kiểu CHỨA (substring) trong nội dung chuyển khoản,
              không phân biệt hoa/thường. Nếu <strong>{next}</strong> ngắn hoặc trùng một từ tiếng Việt không dấu
              thông dụng (vd: TIEN, CHUY, HOAN, THAN…), nó sẽ khớp luôn cả các giao dịch chuyển tiền cá nhân không
              liên quan đến học phí, mỗi giao dịch như vậy khiến LopHoc bị SePay tính phí 500đ. Hãy chọn một từ khóa
              đặc trưng, không phải một từ/âm tiết phổ biến.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Huỷ</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                rotate.mutate(next, { onSuccess: () => setNext('') });
                setConfirmRotate(false);
              }}
            >
              Đổi từ khóa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmResync} onOpenChange={(o) => !o && setConfirmResync(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {resyncExtra.length > 0 ? 'Xoá từ khóa lạ khỏi SePay?' : 'Đẩy whitelist lên SePay?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {resyncExtra.length > 0 ? (
                <>
                  SePay hiện có thêm từ khóa mà hệ thống không quản lý: <strong>[{resyncExtra.join(', ')}]</strong>.
                  Đẩy lại sẽ THAY THẾ toàn bộ whitelist trên SePay bằng đúng danh sách của hệ thống
                  ([{resyncWhitelist.join(', ')}]), nghĩa là các từ khóa lạ trên sẽ bị XOÁ khỏi SePay ngay lập tức.
                  Hóa đơn chưa thanh toán nào có QR mang một trong các từ khóa đó sẽ NGỪNG tự đối soát. Chỉ tiếp tục
                  khi chắc chắn không có QR nào đang lưu hành dùng các từ khóa lạ này.
                </>
              ) : (
                <>
                  Hệ thống sẽ đẩy whitelist [{resyncWhitelist.join(', ')}] lên SePay. Không có từ khóa nào bị xoá,
                  thao tác này chỉ khôi phục lại đúng danh sách hệ thống đang quản lý.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Huỷ</AlertDialogCancel>
            <AlertDialogAction
              className={resyncExtra.length > 0 ? 'bg-red-600 hover:bg-red-700' : undefined}
              disabled={resync.isPending}
              onClick={() => {
                resync.mutate();
                setConfirmResync(false);
              }}
            >
              {resyncExtra.length > 0 ? 'Xoá và đẩy lại' : 'Đẩy lại lên SePay'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={purgeTarget !== null} onOpenChange={(o) => !o && setPurgeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xoá từ khóa {purgeTarget}?</AlertDialogTitle>
            <AlertDialogDescription>
              Hóa đơn chưa thanh toán nào có QR mang từ khóa này sẽ NGỪNG tự đối soát, phụ huynh chuyển tiền mà hệ
              thống không nhận được webhook. Chỉ xoá khi chắc chắn không còn hóa đơn cũ nào đang chờ.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Huỷ</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (purgeTarget) purge.mutate(purgeTarget);
                setPurgeTarget(null);
              }}
            >
              Xoá
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
